import type { BetslipEvent } from '../types/betslip';
import type {
  BetBuilderCalculateResponse,
  BetBuilderMetadata,
  BetBuilderMyBetsGroup,
  BetBuilderSelection,
} from '../types/betBuilder';
import type { BetBuilderLegListItem } from '../components/molecules/BetBuilderLegList';

export const BET_BUILDER_PREFIX = 'bet-builder';

export const getBetBuilderKey = (eventId: string) => `${BET_BUILDER_PREFIX}:${eventId}`;

export const isBetBuilderBet = (bet: Pick<BetslipEvent, 'betBuilder'>) => Boolean(bet.betBuilder);

export const mapMyBetsSelectionToLeg = (group: BetBuilderMyBetsGroup): BetBuilderLegListItem[] =>
  (group.selections ?? [])
    .filter((selection) => !!selection.outcome && !!selection.market)
    .map((selection) => ({
      id: selection.id,
      outcomeId: selection.outcomeId,
      outcomeName: selection.outcome.name,
      outcomeShortName: selection.outcome.shortName,
      marketName: selection.market.name,
      marketShortName: selection.market.shortName,
      marketTypeName: selection.market.marketType?.name ?? '',
      marketDisplayType: selection.market.displayType,
      eventName: group.event?.name ?? '',
      properties: selection.properties ?? selection.outcome.properties,
      specialValues: selection.specialValues ?? selection.outcome.specialValues,
      sportId: selection.market.marketType?.sportId,
    }));

export const mapBetBuilderSelectionsToLegs = (selections: BetBuilderSelection[]): BetBuilderLegListItem[] =>
  selections.map((selection) => ({
    id: selection.outcomeId,
    outcomeId: selection.outcomeId,
    outcomeName: selection.outcomeName,
    outcomeShortName: selection.outcomeShortName,
    marketName: selection.marketName,
    marketShortName: selection.marketShortName,
    marketTypeName: selection.marketType?.name ?? '',
    marketDisplayType: selection.marketDisplayType,
    eventName: selection.eventName,
    properties: selection.properties,
    specialValues: selection.specialValues,
    sportId: selection.marketType?.sportId,
  }));

const toSelection = (bet: BetslipEvent): BetBuilderSelection => ({
  eventId: bet.eventId,
  outcomeId: bet.outcomeId,
  marketId: bet.marketId,
  odds: bet.odds,
  eventName: bet.eventName,
  marketName: bet.marketName,
  marketShortName: bet.marketShortName,
  marketType: bet.marketType,
  marketDisplayType: bet.marketDisplayType,
  outcomeName: bet.outcomeName,
  outcomeShortName: bet.outcomeShortName,
  manualControl: bet.manualControl,
  isLive: bet.isLive,
  providerId: bet.providerId,
  source: bet.source,
  specialValues: bet.specialValues,
  properties: bet.properties,
  singlesStakeAmount: bet.singlesStakeAmount,
  banker: bet.banker,
  disabled: bet.disabled,
});

export const getFlattenedBetslipSelections = (bets: BetslipEvent[]): BetBuilderSelection[] =>
  bets.flatMap((bet) => (bet.betBuilder ? bet.betBuilder.selections : [toSelection(bet)]));

export const findBetBuilderByChildOutcome = (bets: BetslipEvent[], outcomeId: string): BetslipEvent | undefined =>
  bets.find((bet) => bet.betBuilder?.selections.some((selection) => selection.outcomeId === outcomeId));

export const isOutcomeInBetslip = (bets: BetslipEvent[], outcomeId: string) =>
  bets.some((bet) => bet.outcomeId === outcomeId || bet.betBuilder?.selectedOdds.includes(outcomeId));

export const getSameEventSelections = (bets: BetslipEvent[], eventId: string) =>
  getFlattenedBetslipSelections(bets).filter((selection) => selection.eventId === eventId && !selection.disabled);

const haveSameMembers = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false;
  }

  const rightValues = new Set(right);

  return left.every((value) => rightValues.has(value));
};

export const areSameEventSelectionsCurrent = (
  currentBets: Record<string, BetslipEvent>,
  eventId: string,
  outcomeIds: string[]
) => {
  const sameEventOutcomeIds = getSameEventSelections(Object.values(currentBets), eventId).map(
    (selection) => selection.outcomeId
  );

  return haveSameMembers(sameEventOutcomeIds, outcomeIds);
};

export const createBetBuilderBet = (
  eventId: string,
  selections: BetBuilderSelection[],
  response: BetBuilderCalculateResponse
): BetslipEvent => {
  const key = getBetBuilderKey(eventId);
  const anchor = selections[0];
  const selectedOdds = response.selected_odds;
  const comboOdds = response.combo_odds?.toString() ?? '';

  return {
    ...anchor,
    outcomeId: key,
    marketId: key,
    odds: comboOdds,
    outcomeName: 'Bet Builder',
    outcomeShortName: undefined,
    marketName: 'Bet Builder',
    marketShortName: undefined,
    banker: false,
    singlesStakeAmount: undefined,
    providerId: anchor.providerId,
    source: anchor.source,
    betBuilder: {
      id: key,
      eventId,
      selectedOdds,
      comboOdds,
      selections,
    } satisfies BetBuilderMetadata,
  };
};

export const applyBetBuilderResponseToBets = (
  currentBets: Record<string, BetslipEvent>,
  eventId: string,
  response: BetBuilderCalculateResponse,
  requestedOutcomeIds?: string[]
) => {
  const bets = Object.values(currentBets);
  const sameEventSelections = getSameEventSelections(bets, eventId);
  const selectedOdds = new Set(response.selected_odds);
  const selectedSelections = sameEventSelections.filter((selection) => selectedOdds.has(selection.outcomeId));
  const isStaleResponse =
    requestedOutcomeIds !== undefined && !areSameEventSelectionsCurrent(currentBets, eventId, requestedOutcomeIds);
  const hasMissingReturnedSelection = selectedSelections.length !== selectedOdds.size;

  if (response.combo_odds === null || isStaleResponse || hasMissingReturnedSelection || selectedSelections.length < 2) {
    return currentBets;
  }

  const nextBets = { ...currentBets };
  delete nextBets[getBetBuilderKey(eventId)];

  sameEventSelections.forEach((selection) => {
    delete nextBets[selection.outcomeId];
  });

  sameEventSelections
    .filter((selection) => !selectedOdds.has(selection.outcomeId))
    .forEach((selection) => {
      nextBets[selection.outcomeId] = { ...selection, banker: false };
    });

  nextBets[getBetBuilderKey(eventId)] = createBetBuilderBet(eventId, selectedSelections, response);

  return nextBets;
};

export const removeBetBuilderChildFromBets = (
  currentBets: Record<string, BetslipEvent>,
  outcomeId: string
): { nextBets: Record<string, BetslipEvent>; remainingSelections: BetBuilderSelection[] } => {
  const betBuilderBet = findBetBuilderByChildOutcome(Object.values(currentBets), outcomeId);

  if (!betBuilderBet?.betBuilder) {
    return { nextBets: currentBets, remainingSelections: [] };
  }

  const remainingSelections = betBuilderBet.betBuilder.selections.filter(
    (selection) => selection.outcomeId !== outcomeId
  );
  const nextBets = { ...currentBets };
  delete nextBets[betBuilderBet.outcomeId];

  if (remainingSelections.length === 1) {
    const remainingSelection = remainingSelections[0];
    nextBets[remainingSelection.outcomeId] = { ...remainingSelection, banker: false };
  } else if (remainingSelections.length > 1) {
    nextBets[betBuilderBet.outcomeId] = {
      ...betBuilderBet,
      betBuilder: {
        ...betBuilderBet.betBuilder,
        selectedOdds: remainingSelections.map((selection) => selection.outcomeId),
        selections: remainingSelections,
      },
    };
  }

  return { nextBets, remainingSelections };
};

export const breakBetBuilderEventIntoBets = (currentBets: Record<string, BetslipEvent>, eventId: string) => {
  const nextBets = { ...currentBets };
  const sameEventSelections = getSameEventSelections(Object.values(currentBets), eventId);

  Object.values(currentBets).forEach((bet) => {
    if (bet.eventId === eventId) {
      delete nextBets[bet.outcomeId];
    }
  });

  sameEventSelections.forEach((selection) => {
    nextBets[selection.outcomeId] = { ...selection, banker: false };
  });

  return nextBets;
};

export const getBetBuilderSubmitItems = (bets: BetslipEvent[]) =>
  bets
    .filter((bet) => bet.betBuilder)
    .map((bet) => ({
      eventId: bet.eventId,
      selectedOdds: bet.betBuilder?.selectedOdds ?? [],
      comboOdds: bet.odds,
      singlesStakeAmount: Number(bet.singlesStakeAmount || 0) || undefined,
    }));
